import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SellerProvider } from './context/SellerContext';
import toast, { Toaster, ToastBar } from 'react-hot-toast';
import { X } from 'lucide-react';

// Pages
import LoginPage from './pages/Auth/LoginPage';
import AmazonOAuthCallback from './pages/Auth/AmazonOAuthCallback';
import AdsOAuthCallback from './pages/Auth/AdsOAuthCallback';
import ProtectedRoute from './components/shared/ProtectedRoute';

// App Pages
import SellerHubPage from './pages/Admin/SellerHubPage';
import SellerDashboardPage from './pages/Admin/SellerDashboardPage';
import RoleManagementPage from './pages/Admin/RoleManagementPage';
import UserManagementPage from './pages/Admin/UserManagementPage';
import UserAddEdit from './components/Admin/UserManagement/UserAddEdit';
import SalesOverviewPage from './pages/Admin/SalesOverviewPage';
import SellerAccountsPage from './pages/Admin/SellerAccountsPage';
import SellerDetailPage from './pages/Admin/SellerDetailPage';
import SellerMarketplacesPage from './pages/Admin/SellerMarketplacesPage';
import DeliveryActivityPage from './pages/Admin/DeliveryActivityPage';
import FeedbackRequestPage from './pages/Admin/FeedbackRequestPage';
import ProductsPage from './pages/Admin/ProductsPage';
import HijackAlertsPage from './pages/Admin/HijackAlertsPage';
import ReviewsFeedbackPage from './pages/Admin/ReviewsFeedbackPage';
import PolicyWarningsPage from './pages/Admin/PolicyWarningsPage';
import AdPerformancePage from './pages/Admin/AdPerformancePage';
import SettlementReportsPage from './pages/Admin/SettlementReportsPage';
import PricingCalculatorPage from './pages/Admin/PricingCalculatorPage';
import UserViewPage from './components/Admin/UserManagement/UserViewPage';

// Settings Pages
import NotificationManagementPage from './pages/Admin/Settings/NotificationManagement';
import ResetPasswordPage from './pages/Admin/Settings/ResetPasswordPage';
import SchedulerStatusPage from './pages/Admin/Settings/SchedulerStatusPage';
import SellerInfoPage from './pages/Admin/Settings/SellerInfoPage';
import SellerNotificationConfigPage from './pages/Admin/Settings/SellerNotificationConfigPage';
import SettingsWhatsAppPage from './pages/Admin/Settings/SettingsWhatsAppPage';

// Super Admin Module
import SuperAdminLogin from './pages/SuperAdmin/SuperAdminLogin';
import Organizations from './pages/SuperAdmin/Organizations';
import TenantOnboarding from './pages/SuperAdmin/TenantOnboarding';
import BranchDetails from './pages/SuperAdmin/Branchdetails ';

// Root redirect component
function RootRedirect() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#FFF9EE] to-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#f08804] border-t-transparent mb-4" />
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated() ? <Navigate to="/sellers" replace /> : <Navigate to="/login" replace />;
}

function App() {
  const MANAGEMENT_ADMIN_USER_TYPES = ['MANAGEMENT', 'ADMIN', 'SUPER_USER', 'TECHNICAL_USER', 'POWER_USER'];

  return (
    <>
      {/* Toast Configuration */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#fff',
            color: '#0F1111',
            padding: '12px 16px',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            fontWeight: '500',
            fontSize: '13px',
            maxWidth: '400px',
            border: '1px solid #D5D9D9',
          },
          success: {
            duration: 2500,
            iconTheme: {
              primary: '#f08804',
              secondary: '#fff',
            },
            style: {
              background: '#FFF9EE',
              color: '#0F1111',
              border: '1px solid #f08804',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#B12704',
              secondary: '#fff',
            },
            style: {
              background: '#FFF5F5',
              color: '#B12704',
              border: '1px solid #B12704',
            },
          },
          loading: {
            iconTheme: {
              primary: '#232F3E',
              secondary: '#f08804',
            },
            style: {
              background: '#F3F3F3',
              color: '#232F3E',
              border: '1px solid #D5D9D9',
            },
          },
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <div className="flex items-center gap-2 w-full">
                {icon}
                <span className="flex-1">{message}</span>
                {t.type !== 'loading' && (
                  <button
                    onClick={() => toast.dismiss(t.id)}
                    className="p-1 rounded hover:bg-black/10 transition-colors flex-shrink-0"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            )}
          </ToastBar>
        )}
      </Toaster>

      <AuthProvider>
        <SellerProvider>
          <Router>
            <Routes>
              {/* Root - Redirects based on auth status */}
              <Route path="/" element={<RootRedirect />} />

              {/* Auth Routes - Public */}
              <Route path="/login" element={<LoginPage />} />

              {/* Amazon OAuth Callback */}
              <Route path="/sp-api/callback" element={
                <ProtectedRoute><AmazonOAuthCallback /></ProtectedRoute>
              } />

              {/* Amazon Advertising OAuth Callback */}
              <Route path="/ads-api/callback" element={
                <ProtectedRoute><AdsOAuthCallback /></ProtectedRoute>
              } />

              {/* Platform Admin Routes */}
              <Route path="/platform/login" element={<SuperAdminLogin />} />
              <Route path="/platform/organizations" element={<Organizations />} />
              <Route path="/platform/branch/:branchId" element={<BranchDetails />} />
              <Route path="/platform/tenant-setup" element={<TenantOnboarding />} />

              {/* ═══════════════════════════════════════════════════════
                  Seller Hub — Landing Page (My Sellers)
                  ═══════════════════════════════════════════════════════ */}
              <Route
                path="/sellers"
                element={
                  <ProtectedRoute>
                    <SellerHubPage />
                  </ProtectedRoute>
                }
              />

              {/* Legacy dashboard route → now seller-specific dashboard */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <SellerDashboardPage />
                  </ProtectedRoute>
                }
              />

              {/* ═══════════════════════════════════════════════════════
                  Seller-Scoped Feature Pages
                  ═══════════════════════════════════════════════════════ */}

              {/* Sales Overview */}
              <Route
                path="/sales"
                element={
                  <ProtectedRoute>
                    <SalesOverviewPage />
                  </ProtectedRoute>
                }
              />

              {/* Seller Accounts / Marketplace Management */}
              <Route
                path="/seller-accounts"
                element={
                  <ProtectedRoute>
                    <SellerAccountsPage />
                  </ProtectedRoute>
                }
              />

              {/* Seller Detail (Tabbed: Overview, Channels, Notifications) */}
              <Route
                path="/sellers/:id"
                element={
                  <ProtectedRoute>
                    <SellerDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* Marketplaces — redirects to seller detail channels tab */}
              <Route
                path="/marketplaces"
                element={
                  <ProtectedRoute>
                    <SellerMarketplacesPage />
                  </ProtectedRoute>
                }
              />

              {/* Products */}
              <Route
                path="/products"
                element={
                  <ProtectedRoute>
                    <ProductsPage />
                  </ProtectedRoute>
                }
              />

              {/* Hijack Alerts */}
              <Route
                path="/hijack-alerts"
                element={
                  <ProtectedRoute>
                    <HijackAlertsPage />
                  </ProtectedRoute>
                }
              />

              {/* Reviews & Feedback */}
              <Route
                path="/reviews-feedback"
                element={
                  <ProtectedRoute>
                    <ReviewsFeedbackPage />
                  </ProtectedRoute>
                }
              />

              {/* Review Requests (Solicitations) */}
              <Route
                path="/review-requests"
                element={
                  <ProtectedRoute>
                    <FeedbackRequestPage />
                  </ProtectedRoute>
                }
              />

              {/* Policy Warnings */}
              <Route
                path="/policy-warnings"
                element={
                  <ProtectedRoute>
                    <PolicyWarningsPage />
                  </ProtectedRoute>
                }
              />

              {/* Ad Performance */}
              <Route
                path="/ad-performance"
                element={
                  <ProtectedRoute>
                    <AdPerformancePage />
                  </ProtectedRoute>
                }
              />

              {/* Settlement Reports (Reconciliation) */}
              <Route
                path="/settlement-reports"
                element={
                  <ProtectedRoute>
                    <SettlementReportsPage />
                  </ProtectedRoute>
                }
              />

              {/* Pricing Calculator */}
              <Route
                path="/pricing-calculator"
                element={
                  <ProtectedRoute>
                    <PricingCalculatorPage />
                  </ProtectedRoute>
                }
              />

              {/* Delivery Activity */}
              <Route
                path="/delivery-activity"
                element={
                  <ProtectedRoute>
                    <DeliveryActivityPage />
                  </ProtectedRoute>
                }
              />

              {/* ═══════════════════════════════════════════════════════
                  Global Pages (not seller-scoped)
                  ═══════════════════════════════════════════════════════ */}

              {/* Roles & Permissions */}
              <Route
                path="/roles"
                element={
                  <ProtectedRoute>
                    <RoleManagementPage />
                  </ProtectedRoute>
                }
              />

              {/* User Management — MANAGEMENT, ADMIN and admin-equivalent user types */}
              <Route
                path="/users"
                element={
                  <ProtectedRoute requiredUserTypes={MANAGEMENT_ADMIN_USER_TYPES}>
                    <UserManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users/add"
                element={
                  <ProtectedRoute requiredUserTypes={MANAGEMENT_ADMIN_USER_TYPES}>
                    <UserAddEdit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users/edit/:id"
                element={
                  <ProtectedRoute requiredUserTypes={MANAGEMENT_ADMIN_USER_TYPES}>
                    <UserAddEdit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users/view/:id"
                element={
                  <ProtectedRoute requiredUserTypes={MANAGEMENT_ADMIN_USER_TYPES}>
                    <UserViewPage />
                  </ProtectedRoute>
                }
              />

              {/* Settings Sub-pages */}
              <Route
                path="/settings/seller-info"
                element={
                  <ProtectedRoute>
                    <SellerInfoPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/notification-config"
                element={
                  <ProtectedRoute>
                    <SellerNotificationConfigPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/notifications"
                element={
                  <ProtectedRoute>
                    <NotificationManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/password"
                element={
                  <ProtectedRoute>
                    <ResetPasswordPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/scheduler-status"
                element={
                  <ProtectedRoute requiredUserTypes={MANAGEMENT_ADMIN_USER_TYPES}>
                    <SchedulerStatusPage />
                  </ProtectedRoute>
                }
              />

              {/* Profile (legacy route) */}
              <Route
                path="/profile/reset-password"
                element={
                  <ProtectedRoute>
                    <ResetPasswordPage />
                  </ProtectedRoute>
                }
              />
              {/* ═══════════════════════════════════════════════════════
                  Settings → WhatsApp (+ legacy redirects)
                  ═══════════════════════════════════════════════════════ */}
              <Route
                path="/settings/whatsapp"
                element={
                  <ProtectedRoute>
                    <SettingsWhatsAppPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/settings" element={<Navigate to="/settings/whatsapp" replace />} />
              <Route path="/integrations" element={<Navigate to="/settings/whatsapp" replace />} />
              <Route
                path="/integrations/whatsapp"
                element={<Navigate to="/settings/whatsapp" replace />}
              />

              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>
        </SellerProvider>
      </AuthProvider>
    </>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#FFF9EE] via-slate-50 to-violet-50">
      <div className="text-center max-w-md mx-auto px-6">
        <div className="mb-6 relative">
          <div className="text-9xl font-black text-slate-200 select-none">404</div>
          <div className="absolute inset-0 flex items-center justify-center">
            <svg
              className="w-24 h-24 text-[#f08804]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        </div>


        <h1 className="text-3xl font-bold text-gray-900 mb-3">Page Not Found</h1>
        <p className="text-gray-600 mb-8 text-lg">
          Oops! The page you're looking for doesn't exist or has been moved.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="/sellers"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#232F3E] text-white rounded-lg font-semibold hover:bg-[#37475A] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            Go to My Sellers
          </a>
        </div>

        <p className="mt-8 text-sm text-slate-500">
          Need help? Contact support or go back to the home page.
        </p>
      </div>
    </div>
  );
}

export default App;
